import { Injectable } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { filter } from 'rxjs/operators';
import type { IconDef } from '../icons/icon';

export interface BreadcrumbItem {
  label?: string;
  routerLink?: string;
  icon?: string | IconDef;
}

@Injectable({ providedIn: 'root' })
export class BreadcrumbService {

  private readonly breadcrumbSubject = new BehaviorSubject<BreadcrumbItem[]>([]);
  readonly breadcrumbs$ = this.breadcrumbSubject.asObservable();

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute
  ) {
    this.updateBreadcrumbs();
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => this.updateBreadcrumbs());
  }

  private updateBreadcrumbs(): void {
    const breadcrumbs: BreadcrumbItem[] = [];
    breadcrumbs.push({
      icon: 'icon-dashboard',
      label: '',
      routerLink: '/dashboard'
    });

    this.build(this.activatedRoute.root, '', breadcrumbs);

    this.breadcrumbSubject.next(breadcrumbs);
  }

  private build(
    route: ActivatedRoute,
    url: string,
    breadcrumbs: BreadcrumbItem[]
  ): void {

    const children = route.children;
    if (!children || children.length === 0) return;

    for (const child of children) {
      const segment = child.snapshot.url.map(s => s.path).join('/');

      if (!segment) {
        this.build(child, url, breadcrumbs);
        return;
      }

      const nextUrl = `${url}/${segment}`;
      const parentLabel = child.snapshot.data['parentBreadcrumb'];
      const parentRouterLink = child.snapshot.data['parentBreadcrumbLink'];
      const label = child.snapshot.data['breadcrumb'];

      if (
        parentLabel &&
        breadcrumbs[breadcrumbs.length - 1]?.label !== parentLabel
      ) {
        breadcrumbs.push({
          label: parentLabel,
          routerLink: parentRouterLink || '/tenant'
        });
      }

      if (
        label &&
        breadcrumbs[breadcrumbs.length - 1]?.label !== label
      ) {
        breadcrumbs.push({
          label,
          routerLink: nextUrl
        });
      }

      this.build(child, nextUrl, breadcrumbs);
      return;
    }
  }
}
